import type {
  BridgeEvent,
  ChatPostResponse,
  DemoManifestEntry,
  InteractionDetail,
  PagedInteractions,
  RawSessionDetail,
  RawSessionSummary,
  SessionDetail,
  SessionEvent,
  SessionSummary,
} from "./types";
import type { AgentCanvasChatContext } from '@/artifact/types';
import { hydrateSession } from "./types";

export class PaymentRequiredError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PaymentRequiredError';
  }
}

export class ApiError extends Error {
  status: number;
  /** Parsed JSON body of the error response, if any. Carries extra fields
   *  the server includes (e.g. `error_id`, `traceback` in local dev). */
  body?: Record<string, unknown>;

  constructor(message: string, status: number, body?: Record<string, unknown>) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

function parseApiError(text: string, status: number): ApiError {
  let message = text || `HTTP ${status}`;
  let body: Record<string, unknown> | undefined;
  try {
    const json = JSON.parse(text);
    if (json && typeof json === 'object' && !Array.isArray(json)) {
      body = json as Record<string, unknown>;
    }
    let detail = json.detail ?? text;
    if (typeof detail === "string") {
      try {
        const inner = JSON.parse(detail);
        message = inner.message ?? detail;
      } catch {
        message = detail;
      }
    } else {
      message = String(detail);
    }
  } catch {
    // not JSON, use raw text
  }
  return new ApiError(message, status, body);
}

async function readJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const text = await res.text();
    throw parseApiError(text, res.status);
  }
  return (await res.json()) as T;
}

export interface ModelsResponse {
  models: string[];
  default: string;
}

export interface StatusResponse {
  status: string;
  model: string;
  commit_hash: string;
  runtime_count: number;
  time: string;
}

export interface UploadedFile {
  path: string;
  filename: string;
  size: number;
}

function normalizeBaseUrl(baseUrl: string): string {
  return baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
}

function resolveTunnelBaseUrl(httpUrl: string, sfUrl: string): string {
  return normalizeBaseUrl(new URL(httpUrl, sfUrl).toString());
}

// Parse `attachment; filename="foo.log"` (or `filename*=UTF-8''…`) and
// return just the filename. Returns null on miss.
function parseAttachmentFilename(disposition: string): string | null {
  const star = /filename\*\s*=\s*[^']*''([^;]+)/i.exec(disposition);
  if (star) {
    try { return decodeURIComponent(star[1]); } catch { /* fallthrough */ }
  }
  const quoted = /filename\s*=\s*"([^"]+)"/i.exec(disposition);
  if (quoted) return quoted[1];
  const bare = /filename\s*=\s*([^;]+)/i.exec(disposition);
  if (bare) return bare[1].trim();
  return null;
}

// Trigger a browser download for an in-memory Blob via a synthetic
// anchor click. Cleans up the object URL on the next tick.
function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export function uploadFile(
  file: File,
  onProgress?: (percent: number) => void,
  endpointUrl?: string,
): Promise<UploadedFile> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", endpointUrl ?? "/api/upload");

    if (onProgress) {
      xhr.upload.addEventListener("progress", (e) => {
        if (e.lengthComputable) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      });
    }

    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText) as UploadedFile);
        } catch {
          reject(new Error("Invalid response"));
        }
      } else {
        let message = `HTTP ${xhr.status}`;
        try {
          const json = JSON.parse(xhr.responseText);
          if (json.detail) message = json.detail;
        } catch {
          /* not JSON */
        }
        reject(new Error(message));
      }
    });

    xhr.addEventListener("error", () => reject(new Error("Upload failed")));
    xhr.addEventListener("abort", () => reject(new Error("Upload cancelled")));

    const formData = new FormData();
    formData.append("file", file);
    xhr.send(formData);
  });
}

export class LoginClient {
  protected _token: string;

  constructor(token: string) {
    this._token = token;
  }

  get token(): string {
    return this._token;
  }

  setToken(token: string) {
    this._token = token;
  }

}

export class VmClient {
  private _login: LoginClient;
  private _baseUrl: string;
  private _isTunnel: boolean;

  private constructor(login: LoginClient, baseUrl: string, isTunnel: boolean) {
    this._login = login;
    this._baseUrl = baseUrl;
    this._isTunnel = isTunnel;
  }

  get baseUrl(): string {
    return this._baseUrl;
  }

  static async create(login: LoginClient): Promise<VmClient> {
    const directUrl = import.meta.env.VITE_BRIDGE_URL;
    if (directUrl?.trim()) {
      return new VmClient(login, normalizeBaseUrl(directUrl.trim()), false);
    }

    const sfUrl = import.meta.env.VITE_SESSION_FACTORY_URL;
    if (!sfUrl) throw new Error("Missing env: VITE_SESSION_FACTORY_URL or VITE_BRIDGE_URL");

    const maxAttempts = 4;
    let lastError: Error | undefined;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      if (attempt > 0) {
        await new Promise((resolve) => setTimeout(resolve, 500 * attempt));
      }
      const res = await fetch(
        `${sfUrl}/app/agentica-chat?bearer=${encodeURIComponent(login.token)}`,
        { method: "POST" },
      );
      if (res.status === 422) {
        try {
          await readJson(res);
        } catch (err) {
          lastError = err instanceof Error ? err : new Error(String(err));
        }
        continue;
      }
      const data = await readJson<{ http_url: string; machine_id: string }>(res);
      return new VmClient(
        login,
        resolveTunnelBaseUrl(data.http_url, sfUrl),
        true,
      );
    }
    throw lastError ?? new Error("Failed to acquire VM after retries");
  }

  private proxyVmEndpoint(
    path: string,
    params?: Record<string, string>,
  ): string {
    const relativePath = path.startsWith("/") ? path.slice(1) : path;
    const url = new URL(`${this._baseUrl}/${relativePath}`);
    if (this._isTunnel) url.searchParams.set("bearer", this._login.token);
    if (this._login.token) url.searchParams.set("supabase_token", this._login.token);
    if (params) {
      for (const [k, v] of Object.entries(params)) url.searchParams.set(k, v);
    }
    return url.toString();
  }

  uploadFile(
    file: File,
    onProgress?: (percent: number) => void,
  ): Promise<UploadedFile> {
    const url = this.proxyVmEndpoint("/api/upload");
    return uploadFile(file, onProgress, url);
  }

  getDownloadUrl(downloadId: string): string {
    return this.proxyVmEndpoint(`/api/downloads/${downloadId}`);
  }

  getThumbnailUrl(thumbnailPath: string): string {
    return this.proxyVmEndpoint(thumbnailPath);
  }

  getArtifactWebSocketUrl(sessionId: string): string {
    // Get the HTTP endpoint URL using existing proxyVmEndpoint logic
    const httpUrl = this.proxyVmEndpoint("/ws/artifact", {
      session_id: sessionId,
    });

    // Convert HTTP(S) URL to WebSocket URL
    return httpUrl.replace(/^https?:/, (match) =>
      match === "https:" ? "wss:" : "ws:",
    );
  }

  async listSessions(
    limit = 50,
    offset = 0,
    q = "",
  ): Promise<SessionSummary[]> {
    const params: Record<string, string> = {
      limit: String(limit),
      offset: String(offset),
    };
    if (q.trim()) params.q = q.trim();
    const res = await fetch(this.proxyVmEndpoint("/api/sessions", params));
    const raw = await readJson<RawSessionSummary[]>(res);
    return raw.map(hydrateSession) as SessionSummary[];
  }

  async fetchDemoManifest(): Promise<DemoManifestEntry[]> {
    const res = await fetch(this.proxyVmEndpoint("/api/demos/manifest"));
    return readJson<DemoManifestEntry[]>(res);
  }

  async activateDemo(name: string): Promise<string> {
    const res = await fetch(this.proxyVmEndpoint(`/api/demos/${encodeURIComponent(name)}/activate`), {
      method: "POST",
    });
    const data = await readJson<{ session_id: string }>(res);
    return data.session_id;
  }

  async getSession(sessionId: string): Promise<SessionDetail> {
    const res = await fetch(this.proxyVmEndpoint(`/api/sessions/${sessionId}`));
    return hydrateSession(await readJson<RawSessionDetail>(res)) as SessionDetail;
  }
  /**
   * Start a new agent run. Returns {run_id, session_id} as plain JSON.
   * Run events arrive via the per-session content stream
   * (streamSessionContentEvents).
   */
  async startRun(
    message: string,
    sessionId: string | null,
    opts?: {
      model?: string;
      filePaths?: string[];
      agentCanvas?: AgentCanvasChatContext | null;
    },
  ): Promise<ChatPostResponse> {
    const payload: Record<string, unknown> = { message, session_id: sessionId };
    if (opts?.model) payload.model = opts.model;
    if (opts?.filePaths?.length) payload.file_paths = opts.filePaths;
    if (opts?.agentCanvas) payload.agent_canvas = opts.agentCanvas;

    const chatUrl = this.proxyVmEndpoint("/api/chat");
    console.debug('[api] POST /api/chat', { sessionId, model: opts?.model });
    const res = await fetch(chatUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    console.debug('[api] /api/chat ->', res.status);

    if (!res.ok) {
      const text = await res.text();
      if (res.status === 402) {
        let msg = text || `HTTP ${res.status}`;
        try {
          const json = JSON.parse(text);
          if (json.detail) msg = json.detail;
        } catch { /* not JSON */ }
        throw new PaymentRequiredError(msg);
      }
      throw parseApiError(text, res.status);
    }
    // Server may return either a JSON `ChatPostResponse` or a file
    // download (e.g. `$$ download logs|db|panel`). Detect the latter
    // via Content-Disposition: attachment and stream the response into
    // a Blob → anchor click → silently consume here, returning a
    // toast so the UI confirms the action.
    const disposition = res.headers.get('content-disposition') || '';
    if (/attachment/i.test(disposition)) {
      const filename = parseAttachmentFilename(disposition) || 'download';
      const blob = await res.blob();
      triggerBlobDownload(blob, filename);
      return { text: `Downloaded ${filename}`, level: 'success' } satisfies ChatPostResponse;
    }
    return (await res.json()) as ChatPostResponse;
  }

  /**
   * Open a per-session SSE connection that streams run content events.
   *
   * Unlike the old per-run stream, this subscribes at the session level.
   * When a follow-up message starts a new run on the same session, its
   * events flow through this same stream — no reconnection needed.
   *
   * Pass ``afterSeq`` to resume from the last received event seq.
   * The stream stays open until aborted or the session is deleted.
   */
  async streamSessionContentEvents(
    sessionId: string,
    afterSeq: number,
    onEvent: (event: BridgeEvent) => void,
    signal?: AbortSignal,
  ): Promise<void> {
    const url = this.proxyVmEndpoint(
      `/api/sessions/${sessionId}/events`,
      { after_seq: String(afterSeq) },
    );
    const res = await fetch(url, { signal, cache: 'no-store' });
    if (!res.ok) {
      const text = await res.text();
      throw parseApiError(text, res.status);
    }
    if (!res.body) return;

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      while (true) {
        const separator = buffer.indexOf("\n\n");
        if (separator === -1) break;

        const rawEvent = buffer.slice(0, separator);
        buffer = buffer.slice(separator + 2);

        for (const line of rawEvent.split("\n")) {
          if (!line.startsWith("data: ")) continue;
          const jsonStr = line.slice(6);
          try {
            onEvent(JSON.parse(jsonStr) as BridgeEvent);
          } catch {
            // Ignore malformed events
          }
        }
      }
    }
  }

  async listInteractions(
    sessionId: string,
    limit = 20,
    offset = 0,
  ): Promise<PagedInteractions> {
    const res = await fetch(
      this.proxyVmEndpoint(`/api/sessions/${sessionId}/interactions`, {
        limit: String(limit),
        offset: String(offset),
      }),
    );
    return readJson<PagedInteractions>(res);
  }

  async getInteraction(interactionId: string): Promise<InteractionDetail> {
    const res = await fetch(
      this.proxyVmEndpoint(`/api/interactions/${interactionId}`),
    );
    return readJson<InteractionDetail>(res);
  }

  async deleteArtifactPage(sessionId: string, pageIndex: number): Promise<void> {
    const res = await fetch(
      this.proxyVmEndpoint(`/api/sessions/${sessionId}/artifact-pages/${pageIndex}`),
      { method: "DELETE" },
    );
    await readJson<{ status: string }>(res);
  }

  async deleteSession(sessionId: string): Promise<void> {
    const res = await fetch(
      this.proxyVmEndpoint(`/api/sessions/${sessionId}`),
      { method: "DELETE" },
    );
    await readJson<{ status: string }>(res);
  }

  async cancelSessionRun(sessionId: string): Promise<void> {
    const res = await fetch(
      this.proxyVmEndpoint(`/api/sessions/${sessionId}/cancel`),
      { method: "POST" },
    );
    await readJson<{ status: string }>(res);
  }

  async renameSession(sessionId: string, title: string): Promise<void> {
    const res = await fetch(
      this.proxyVmEndpoint(`/api/sessions/${sessionId}`),
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title }),
      },
    );
    await readJson<{ status: string }>(res);
  }

  async pinSession(sessionId: string, pinned: boolean): Promise<void> {
    const session = await this.getSession(sessionId);
    const presentation = { ...(session.presentation ?? {}), pinned };
    const res = await fetch(
      this.proxyVmEndpoint(`/api/sessions/${sessionId}`),
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ presentation: JSON.stringify(presentation) }),
      },
    );
    await readJson<{ status: string }>(res);
  }

  async stopSession(sessionId: string): Promise<void> {
    const res = await fetch(
      this.proxyVmEndpoint(`/api/sessions/${sessionId}/stop`),
      { method: "POST" },
    );
    await readJson<{ status: string }>(res);
  }

  async startSession(sessionId: string): Promise<void> {
    const res = await fetch(
      this.proxyVmEndpoint(`/api/sessions/${sessionId}/start`),
      { method: "POST" },
    );
    await readJson<{ status: string }>(res);
  }

  async fetchModels(): Promise<ModelsResponse> {
    const res = await fetch(this.proxyVmEndpoint("/api/models"));
    return readJson<ModelsResponse>(res);
  }

  async fetchStatus(): Promise<StatusResponse> {
    const res = await fetch(this.proxyVmEndpoint("/api/status"));
    return readJson<StatusResponse>(res);
  }

  getAuthStartUrl(sessionId: string, provider: string = "google", authStartPath?: string): string {
    if (authStartPath) {
      return this.proxyVmEndpoint(authStartPath);
    }
    return this.proxyVmEndpoint(`/api/auth/${provider}/start`, {
      session_id: sessionId,
    });
  }

  async streamSessionEvents(
    onEvent: (event: SessionEvent) => void,
    signal?: AbortSignal,
    onConnected?: () => void,
  ): Promise<void> {
    const url = this.proxyVmEndpoint("/api/sessions/events");
    const res = await fetch(url, { signal, cache: 'no-store' });
    if (!res.ok) {
      const text = await res.text();
      throw parseApiError(text, res.status);
    }
    if (!res.body) return;

    onConnected?.();

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      while (true) {
        const separator = buffer.indexOf("\n\n");
        if (separator === -1) break;

        const rawEvent = buffer.slice(0, separator);
        buffer = buffer.slice(separator + 2);

        for (const line of rawEvent.split("\n")) {
          if (!line.startsWith("data: ")) continue;
          try {
            const parsed = JSON.parse(line.slice(6));
            if (parsed.type === 'session_updated') {
              parsed.session = hydrateSession(parsed.session);
            }
            onEvent(parsed as SessionEvent);
          } catch {
            // ignore malformed events
          }
        }
      }
    }
  }
}
